/// <reference types="gtag.js" />

import type { NextWebVitalsMetric } from "next/app"

/**
 * THE analytics module — every gtag touchpoint lives here (or in
 * `lib/consent`, which owns Consent Mode v2 exclusively; keep consent logic
 * OUT of this file so the two can never drift apart).
 *
 * Load order (D22): _document's inline CONSENT_BOOTSTRAP sets the
 * denied-by-default baseline → _app renders <GoogleAnalytics/> (gated on
 * GA_TRACKING_ID, the sweetandbitter pattern) → the banner upgrades consent.
 *
 * Pure .ts on purpose: the one JSX line (the GoogleAnalytics gate) lives in
 * _app, which keeps this file identical in kind to sweetandbitter's
 * lib/utilities/analytics.ts.
 *
 * The v4 site's `event` function was COMMENTED OUT — every "tracked" event
 * silently went nowhere. These are the conversion events the Ads campaign
 * (15/09) is bought against:
 *
 *   phone_click        — every phone affordance, labelled by placement
 *   email_click        — every mailto affordance, same placement taxonomy
 *                        (footer, rich-text); a written enquiry is a softer
 *                        conversion than a call but a conversion still
 *   contact_submit     — contact form success
 *   appointment_submit — callback form success
 *   eopyy_click        — the header badge (measures insurance-led traffic)
 *
 * Page views: @next/third-parties fires them on route change — never fire
 * them manually or every view double-counts.
 */
export const GA_TRACKING_ID =
  process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_TRACKING_ID ?? ""

// Google Tag Manager container (owner, 2026-09-11) — loaded ALONGSIDE the
// direct gtag.js above, not instead of it: our conversion events fire via
// gtag(), which GTM tags cannot trigger on. The container is for Ads-side
// tags (conversion linker etc.). It must NOT hold a GA4 tag for
// G-D1EK473N1E or every page view double-counts.
export const GTM_CONTAINER_ID =
  process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID ?? ""

type WindowWithGtag = Window & { gtag?: Gtag.Gtag }

/**
 * First-party conversion counting (P11, 2026-09-17). GA above is
 * consent-gated — with denied-by-default Consent Mode every visitor who
 * declines the banner is invisible, which biases any «how many people tried
 * to call us» report. These events ALSO beacon to /api/track → CMS
 * site-events, carrying NO personal data (no IP, no UA, no cookie, no
 * session id — Plausible-style anonymous statistics), so the count is
 * complete regardless of consent and the monthly report
 * (CMS scripts/monthly-report.mjs) reads a truthful baseline.
 *
 * Only the conversion taxonomy is beaconed — behavioural events (search_*,
 * back_to_top, coreWebVitals) stay GA-only.
 */
const FIRST_PARTY_EVENTS = new Set([
  "phone_click", "email_click", "map_directions_click", "map_locate_click",
  "map_zoom", "eopyy_click", "review_click", "review_write_click",
  "appointment_open", "appointment_submit",
  "contact_submit", "career_submit", "newsletter_submit",
])

const ATTRIB_KEY = "pw_attrib"

/**
 * First-touch attribution, captured ONCE per tab on landing (called from
 * _app) — by the time someone clicks «call» three pages later,
 * document.referrer and the utm_* params are long gone. sessionStorage:
 * per-tab, dies with it, readable without consent (it stores campaign
 * labels, not identity). gclid is reduced to a BOOLEAN — the click id
 * itself is an identifier and has no place in an anonymous store.
 */
export const captureAttribution = () => {
  if (typeof window === "undefined") return
  try {
    if (sessionStorage.getItem(ATTRIB_KEY)) return
    const q = new URLSearchParams(window.location.search)
    let referrerDomain = ""
    try {
      const ref = document.referrer ? new URL(document.referrer).hostname : ""
      if (ref && ref !== window.location.hostname) referrerDomain = ref
    } catch { /* unparseable referrer — treat as none */ }
    sessionStorage.setItem(ATTRIB_KEY, JSON.stringify({
      utmSource: q.get("utm_source") ?? "",
      utmMedium: q.get("utm_medium") ?? "",
      utmCampaign: q.get("utm_campaign") ?? "",
      hasGclid: q.has("gclid"),
      referrerDomain,
    }))
  } catch { /* storage blocked — attribution degrades to blank, events still count */ }
}

const deviceClass = (): "mobile" | "tablet" | "desktop" => {
  const w = window.innerWidth
  return w < 768 ? "mobile" : w < 1024 ? "tablet" : "desktop"
}

const trackFirstParty = (action: string, params: Record<string, unknown>) => {
  try {
    let attrib: Record<string, unknown> = {}
    try { attrib = JSON.parse(sessionStorage.getItem(ATTRIB_KEY) ?? "{}") } catch { /* blank */ }
    const payload = JSON.stringify({
      event: action,
      // the existing call sites label placement via event_label
      placement: typeof params.event_label === "string" ? params.event_label : "",
      path: window.location.pathname,
      device: deviceClass(),
      ...attrib,
    })
    // sendBeacon survives the navigation a tel:/mailto: click triggers;
    // keepalive fetch is the fallback for browsers without it
    if (!navigator.sendBeacon?.("/api/track", new Blob([payload], { type: "application/json" }))) {
      fetch("/api/track", { method: "POST", body: payload, keepalive: true,
        headers: { "Content-Type": "application/json" } }).catch(() => {})
    }
  } catch { /* counting must never break the click it counts */ }
}

/** Guarded event helper — safe during SSR and before gtag.js hydrates. */
export const event = (
  action: Gtag.EventNames | string,
  params: Record<string, unknown> = {}
) => {
  if (typeof window === "undefined") return
  ;(window as WindowWithGtag).gtag?.("event", action, params)
  if (FIRST_PARTY_EVENTS.has(action as string)) trackFirstParty(action as string, params)
}

/** kept for legacy callers; @next/third-parties owns pageviews */
export const pageview = (_url: URL | string) => {}

/** Core Web Vitals → GA4, re-exported by _app's `reportWebVitals`. */
export const reportWebVitalsToGA = (metric: NextWebVitalsMetric) => {
  event("coreWebVitals", {
    event_category:
      metric.label === "web-vital" ? "Web Vitals" : "Next.js custom metric",
    // GA wants integers; CLS is a small float
    value: Math.round(metric.name === "CLS" ? metric.value * 1000 : metric.value),
    event_action: metric.name,
    event_label: metric.id, // unique to the current page load
    non_interaction: true, // must not affect bounce rate
  })
}
